import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../config/supabase';
import type { Rental } from '../../config/supabase';

// Query Keys
export const rentalsKeys = {
  all: ['rentals'] as const,
  lists: () => [...rentalsKeys.all, 'list'] as const,
  list: (page?: number, pageSize?: number, search?: string, mode?: string, sortBy?: string, sortOrder?: 'asc' | 'desc', categoryFilter?: string, statusFilter?: string, dateFromFilter?: string, dateToFilter?: string) => 
    [...rentalsKeys.lists(), { page, pageSize, search, mode, sortBy, sortOrder, categoryFilter, statusFilter, dateFromFilter, dateToFilter }] as const,
  calendar: () => [...rentalsKeys.all, 'calendar'] as const,
  returns: () => [...rentalsKeys.all, 'returns'] as const,
};

// Fetch rentals with pagination (table view)
export const useRentals = (page: number = 1, pageSize: number = 10, search: string = '', mode: string = 'table', sortBy?: string, sortOrder: 'asc' | 'desc' = 'desc', categoryFilter?: string, statusFilter?: string, dateFromFilter?: string, dateToFilter?: string) => {
  return useQuery({
    queryKey: rentalsKeys.list(page, pageSize, search, mode, sortBy, sortOrder, categoryFilter, statusFilter, dateFromFilter, dateToFilter),
    queryFn: async () => {
      if (mode === 'table') {
        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;

        let query = supabase
          .from('rentals')
          .select(`
            *,
            dresses (
              id,
              name,
              barcode,
              color,
              size,
              rental_price,
              photo_url,
              category_id,
              branch_id,
              categories (
                id,
                name,
                color
              ),
              branches (
                id,
                name
              )
            ),
            customers (
              id,
              name,
              email,
              phone
            )
          `, { count: 'exact' });

        // Apply search filter if search term exists
        if (search.trim()) {
          query = query.or(`dresses.name.ilike.%${search}%,dresses.barcode.ilike.%${search}%,customers.name.ilike.%${search}%`);
        }

        // Apply filters
        if (categoryFilter) {
          // We need to handle this differently - Supabase doesn't support direct filtering on nested relations
          // We'll need to get dress IDs first, then filter rentals
          const { data: dressesWithCategory } = await supabase
            .from('dresses')
            .select('id')
            .eq('category_id', categoryFilter);
          
          if (dressesWithCategory && dressesWithCategory.length > 0) {
            const dressIds = dressesWithCategory.map(d => d.id);
            query = query.in('dress_id', dressIds);
          } else {
            // No dresses found with this category, return empty result
            query = query.eq('id', 'no-match-found');
          }
        }
        
        if (statusFilter) {
          query = query.eq('status', statusFilter);
        }
        
        if (dateFromFilter) {
          query = query.gte('rental_date', dateFromFilter);
        }
        
        if (dateToFilter) {
          query = query.lte('rental_date', dateToFilter);
        }

        // Apply sorting - use server-side for simple fields, client-side for complex ones
        if (sortBy) {
          const ascending = sortOrder === 'asc';

          
          switch (sortBy) {
            case 'price':
              query = query.order('rental_price', { ascending, nullsFirst: false });
              break;
            case 'status':
              query = query.order('status', { ascending, nullsFirst: false });
              break;
            case 'rental_date':
            case 'return_date':
            case 'customer':
            case 'dress':
            case 'category':
              // For date and related table sorting, use client-side for reliability
              // Just order by default for now and sort client-side
              query = query.order('rental_date', { ascending: false });
              break;
            default:
              query = query.order('rental_date', { ascending: false });
          }
        } else {
          query = query.order('rental_date', { ascending: false });
        }

        query = query.range(from, to);

        const { data, error, count } = await query;

        if (error) throw error;
        
        let rentals = data || [];
        
        // Apply client-side sorting for complex fields (dates and related tables)
        if (sortBy && ['customer', 'dress', 'rental_date', 'return_date', 'category'].includes(sortBy)) {

          
          rentals = [...rentals].sort((a, b) => {
            let comparison = 0;
            
            switch (sortBy) {
              case 'customer':
                const customerA = (a.customers as any)?.name || '';
                const customerB = (b.customers as any)?.name || '';
                comparison = customerA.localeCompare(customerB);
                break;
                
              case 'dress':
                const dressA = (a.dresses as any)?.name || '';
                const dressB = (b.dresses as any)?.name || '';
                comparison = dressA.localeCompare(dressB);
                break;
                
              case 'rental_date':
                const dateA = new Date(a.rental_date);
                const dateB = new Date(b.rental_date);
                comparison = dateA.getTime() - dateB.getTime();
                break;
                
              case 'return_date':
                const returnA = new Date(a.expected_return_date);
                const returnB = new Date(b.expected_return_date);
                comparison = returnA.getTime() - returnB.getTime();
                break;
                
              case 'category':
                const categoryA = (a.dresses as any)?.categories?.name || '';
                const categoryB = (b.dresses as any)?.categories?.name || '';
                comparison = categoryA.localeCompare(categoryB);
                break;
            }
            
            return sortOrder === 'asc' ? comparison : -comparison;
          });
          

        }
        
        return {
          rentals,
          totalCount: count || 0,
        };
      } else {
        // For calendar and returns view, fetch all data
        let query = supabase
          .from('rentals')
          .select(`
            *,
            dresses (
              id,
              name,
              barcode,
              color,
              size,
              rental_price,
              photo_url,
              category_id,
              branch_id,
              categories (
                id,
                name,
                color
              ),
              branches (
                id,
                name
              )
            ),
            customers (
              id,
              name,
              email,
              phone
            )
          `);

        if (search.trim()) {
          query = query.or(`dresses.name.ilike.%${search}%,dresses.barcode.ilike.%${search}%,customers.name.ilike.%${search}%`);
        }

        query = query.order('rental_date', { ascending: false });

        const { data, error } = await query;

        if (error) throw error;
        
        return {
          rentals: data || [],
          totalCount: data?.length || 0,
        };
      }
    },
    staleTime: 1 * 60 * 1000, // 1 minute for rentals since they change more frequently
  });
};

// Fetch all rentals for calendar view
export const useRentalsCalendar = () => {
  return useQuery({
    queryKey: rentalsKeys.calendar(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rentals')
        .select(`
          *,
          dresses (
            id,
            name,
            barcode,
            rental_price,
            photo_url,
            branch_id,
            branches (
              id,
              name
            )
          ),
          customers (
            id,
            name,
            email,
            phone
          )
        `)
        .order('rental_date', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    staleTime: 1 * 60 * 1000,
  });
};

// Fetch upcoming returns
export const useUpcomingReturns = () => {
  return useQuery({
    queryKey: rentalsKeys.returns(),
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0];
      const nextWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('rentals')
        .select(`
          *,
          dresses (
            id,
            name,
            barcode,
            rental_price,
            photo_url,
            branch_id,
            branches (
              id,
              name
            )
          ),
          customers (
            id,
            name,
            email,
            phone
          )
        `)
        .gte('return_date', today)
        .lte('return_date', nextWeek)
        .eq('status', 'active')
        .order('return_date');

      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Create rental mutation
export const useCreateRental = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rentalData: Omit<Rental, 'id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('rentals')
        .insert(rentalData)
        .select(`
          *,
          dresses (
            id,
            name,
            barcode,
            color,
            size,
            rental_price,
            photo_url,
            branch_id,
            branches (
              id,
              name
            )
          ),
          customers (
            id,
            name,
            email,
            phone
          )
        `)
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidate all rental queries
      queryClient.invalidateQueries({ queryKey: rentalsKeys.all });
    },
  });
};

// Update rental mutation
export const useUpdateRental = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Rental> & { id: string }) => {
      const { data, error } = await supabase
        .from('rentals')
        .update(updates)
        .eq('id', id)
        .select(`
          *,
          dresses (
            id,
            name,
            barcode,
            color,
            size,
            rental_price,
            photo_url,
            branch_id,
            branches (
              id,
              name
            )
          ),
          customers (
            id,
            name,
            email,
            phone
          )
        `)
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rentalsKeys.all });
    },
  });
};

// Delete rental mutation
export const useDeleteRental = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('rentals')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rentalsKeys.all });
    },
  });
};

// Mark rental as returned
export const useReturnRental = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('rentals')
        .update({ 
          status: 'returned',
          return_date: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rentalsKeys.all });
    },
  });
}; 